import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, X } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { culinaryContent as c } from "../mock";

const Culinary = () => {
  const [showVeg, setShowVeg] = useState(false);
  const [showNonVeg, setShowNonVeg] = useState(false);
  const [showSit, setShowSit] = useState(false);
  const [zoom, setZoom] = useState(null);
  return (
    <div className="min-h-screen flex flex-col bg-background-ivory">
      <Header variant="dark" />

      {/* Hero */}
      <section className="relative h-screen w-full overflow-hidden">
        <img
          src={c.heroImage}
          alt="A chef sketching a dish at the atelier workspace"
          className="absolute inset-0 w-full h-full object-cover"
          decoding="async"
          style={{ filter: "brightness(1.13) contrast(1.02)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/45" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-margin-mobile">
          <p className="font-inter text-eyebrow text-ink-on-dark/80 uppercase mb-6 animate-fade-in">
            {c.chapter}
          </p>
          <h1 className="font-cormorant text-[41px] leading-none md:text-[66px] text-ink-on-dark mb-8 animate-fade-in">
            {c.title}
          </h1>
          <div className="w-16 h-px bg-brand-gold mb-8" />
          <p className="font-inter text-eyebrow text-ink-on-dark/90 uppercase tracking-[0.3em]">
            {c.heroSubtitle}
          </p>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 text-ink-on-dark/70 animate-bounce">
          <ChevronDown className="h-6 w-6" />
        </div>
      </section>

      {/* Intro */}
      <section className="px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal as="p" className="font-inter text-eyebrow text-brand-secondary uppercase mb-8">
            {c.intro.eyebrow}
          </Reveal>
          <Reveal
            as="p"
            delay={120}
            className="font-cormorant text-[19px] md:text-[23px] leading-snug text-brand-ink"
          >
            {c.intro.body}
          </Reveal>
          <Reveal delay={240} className="w-12 h-px bg-brand-gold mx-auto mt-12" />
        </div>

        {/* The Sketchbook — separate framed attachment */}
        <div className="max-w-4xl mx-auto mt-24 grid grid-cols-1 md:grid-cols-12 gap-gutter md:gap-16 items-center">
          <Reveal className="col-span-1 md:col-span-5 md:col-start-2">
            <div className="bg-white p-3 border border-hairline-beige shadow-md rotate-[-2deg]">
              <div className="w-full aspect-[4/5] overflow-hidden bg-white">
                <img
                  src="/culinary-sketch.jpg"
                  alt="Original hand-drawn dish sketches from the atelier"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </Reveal>
          <div className="col-span-1 md:col-span-4 md:col-start-8">
            <Reveal
              as="p"
              className="font-inter text-eyebrow text-brand-secondary uppercase mb-5"
            >
              From the Sketchbook
            </Reveal>
            <Reveal
              as="h3"
              delay={100}
              className="font-cormorant text-[27px] leading-tight text-brand-ink mb-4"
            >
              Where Every Dish Begins
            </Reveal>
            <Reveal as="p" delay={180} className="font-cormorant text-body-md text-deep-taupe">
              Each plate is first drawn by hand — form, tension and balance resolved
              on paper long before the first ingredient is sourced.
            </Reveal>
          </div>
        </div>
      </section>

      {/* Signature dishes */}
      <section className="px-margin-mobile md:px-margin-desktop pb-narrative-gap flex flex-col gap-24 md:gap-narrative-gap">
        {c.dishes.map((dish, i) => {
          const imageLeft = i % 2 === 0;
          return (
            <div
              key={dish.numeral}
              className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-center"
            >
              <Reveal
                className={`col-span-1 md:col-span-7 ${
                  imageLeft ? "md:order-1" : "md:order-2 md:col-start-6"
                }`}
              >
                <div className="w-full aspect-[4/3] overflow-hidden group bg-cover-ivory">
                  <img
                    src={dish.image}
                    alt={dish.name}
                    onClick={() => setZoom({ src: dish.image, alt: dish.name })}
                    className="w-full h-full object-cover cursor-zoom-in transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </Reveal>
              <Reveal
                delay={120}
                className={`col-span-1 md:col-span-4 ${
                  imageLeft ? "md:order-2 md:col-start-9" : "md:order-1 md:col-start-1"
                }`}
              >
                <span className="font-cormorant text-[64px] leading-none text-hairline-beige block mb-4">
                  {dish.numeral}
                </span>
                <h2 className="font-cormorant text-headline-md text-brand-ink mb-5">
                  {dish.name}
                </h2>
                <p className="font-cormorant text-body-md text-deep-taupe">
                  {dish.note}
                </p>
              </Reveal>
            </div>
          );
        })}
      </section>

      {/* The Chapters — menu selections */}
      <section className="px-margin-mobile md:px-margin-desktop pb-narrative-gap">
        <div className="flex flex-col items-center mb-16">
          <Reveal
            as="h2"
            className="font-cormorant text-[27px] md:text-[31px] text-brand-ink mb-6"
          >
            {c.menuHeading}
          </Reveal>
          <Reveal delay={100} className="w-px h-16 bg-brand-gold" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter max-w-6xl mx-auto">
          {c.menuChapters.map((ch, i) => (
            <Reveal
              key={ch.title}
              delay={i * 120}
              className="border border-hairline-beige p-8 md:p-10 flex flex-col group"
            >
              <p className={`font-inter text-eyebrow uppercase mb-6 ${ch.eyebrowColor}`}>
                {ch.eyebrow}
              </p>
              <h3 className="font-cormorant text-[27px] leading-tight text-brand-ink mb-6">
                {ch.title}
              </h3>
              <p className="font-cormorant text-body-md text-deep-taupe mb-8 flex-grow">
                {ch.body}
              </p>
              <div className="border-t border-hairline-beige pt-6 flex items-center justify-between">
                {ch.title === "The Vegetarian Repertoire" ? (
                  <button
                    type="button"
                    onClick={() => setShowVeg((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    data-testid="veg-explore-toggle"
                    aria-expanded={showVeg}
                  >
                    <span className="font-inter text-label-sm uppercase tracking-widest text-brand-ink hover:text-bright-gold-on-dark transition-colors">
                      {showVeg ? "Hide Selection" : ch.cta}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-brand-gold transition-transform duration-300 ${
                        showVeg ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : ch.title === "The Non-Vegetarian Ledger" ? (
                  <button
                    type="button"
                    onClick={() => setShowNonVeg((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    data-testid="nonveg-explore-toggle"
                    aria-expanded={showNonVeg}
                  >
                    <span className="font-inter text-label-sm uppercase tracking-widest text-brand-ink hover:text-bright-gold-on-dark transition-colors">
                      {showNonVeg ? "Hide Selection" : ch.cta}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-brand-gold transition-transform duration-300 ${
                        showNonVeg ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : ch.title === "Sit-Down Protocol" ? (
                  <button
                    type="button"
                    onClick={() => setShowSit((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    data-testid="sit-explore-toggle"
                    aria-expanded={showSit}
                  >
                    <span className="font-inter text-label-sm uppercase tracking-widest text-brand-ink hover:text-bright-gold-on-dark transition-colors">
                      {showSit ? "Hide Selection" : ch.cta}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-brand-gold transition-transform duration-300 ${
                        showSit ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : (
                  <>
                    <span className="font-inter text-label-sm uppercase tracking-widest text-brand-ink group-hover:text-bright-gold-on-dark transition-colors">
                      {ch.cta}
                    </span>
                    <ArrowRight className="w-4 h-4 text-brand-ink transition-transform duration-300 group-hover:translate-x-1 group-hover:text-bright-gold-on-dark" />
                  </>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        {/* Vegetarian Repertoire — revealed via Explore Selection */}
        {showVeg && (
          <div
            className="max-w-6xl mx-auto mt-20 pt-16 border-t border-hairline-beige"
            data-testid="veg-repertoire-panel"
          >
            <div className="max-w-2xl mb-14">
              <p className="font-inter text-eyebrow uppercase tracking-widest text-veg-green mb-4">
                {c.vegRepertoire.eyebrow}
              </p>
              <h3 className="font-cormorant text-[24px] md:text-[32px] leading-tight text-brand-ink mb-5">
                {c.vegRepertoire.heading}
              </h3>
              <p className="font-cormorant text-body-md text-deep-taupe">
                {c.vegRepertoire.body}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter items-start">
              {c.vegRepertoire.items.map((it) => (
                <div key={it.name} className="group" data-testid={`veg-dish-${it.name}`}>
                  <div className={`w-full ${it.aspect} overflow-hidden mb-6 bg-cover-ivory`}>
                    <img
                      src={it.image}
                      alt={it.name}
                      onClick={() => setZoom({ src: it.image, alt: it.name })}
                      className="w-full h-full object-cover cursor-zoom-in transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h4 className="font-cormorant text-[22px] md:text-[24px] leading-tight text-brand-ink mb-3">
                    {it.name}
                  </h4>
                  <p className="font-cormorant text-body-md text-deep-taupe">
                    {it.note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Non-Vegetarian Ledger — revealed via Explore Selection */}
        {showNonVeg && (
          <div
            className="max-w-6xl mx-auto mt-20 pt-16 border-t border-hairline-beige"
            data-testid="nonveg-repertoire-panel"
          >
            <div className="max-w-2xl mb-14">
              <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-4">
                {c.nonVegRepertoire.eyebrow}
              </p>
              <h3 className="font-cormorant text-[24px] md:text-[32px] leading-tight text-brand-ink mb-5">
                {c.nonVegRepertoire.heading}
              </h3>
              <p className="font-cormorant text-body-md text-deep-taupe">
                {c.nonVegRepertoire.body}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-gutter items-start">
              {c.nonVegRepertoire.items.map((it) => (
                <div key={it.name} className="group" data-testid={`nonveg-dish-${it.name}`}>
                  <div className={`w-full ${it.aspect} overflow-hidden mb-6 bg-cover-ivory`}>
                    <img
                      src={it.image}
                      alt={it.name}
                      onClick={() => setZoom({ src: it.image, alt: it.name })}
                      className="w-full h-full object-cover cursor-zoom-in transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h4 className="font-cormorant text-[21px] md:text-[23px] leading-tight text-brand-ink mb-3">
                    {it.name}
                  </h4>
                  <p className="font-cormorant text-body-md text-deep-taupe">
                    {it.note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sit-Down Protocol — revealed via Explore Selection */}
        {showSit && (
          <div
            className="max-w-6xl mx-auto mt-20 pt-16 border-t border-hairline-beige"
            data-testid="sit-repertoire-panel"
          >
            <div className="max-w-2xl mb-14">
              <p className="font-inter text-eyebrow uppercase tracking-widest text-muted-taupe mb-4">
                {c.sitDown.eyebrow}
              </p>
              <h3 className="font-cormorant text-[24px] md:text-[32px] leading-tight text-brand-ink mb-5">
                {c.sitDown.heading}
              </h3>
              <p className="font-cormorant text-body-md text-deep-taupe">
                {c.sitDown.body}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter items-start">
              {c.sitDown.items.map((it) => (
                <div key={it.name} className="group" data-testid={`sit-dish-${it.name}`}>
                  <div className={`w-full ${it.aspect} overflow-hidden mb-6 bg-cover-ivory`}>
                    <img
                      src={it.image}
                      alt={it.name}
                      onClick={() => setZoom({ src: it.image, alt: it.name })}
                      className="w-full h-full object-cover cursor-zoom-in transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h4 className="font-cormorant text-[22px] md:text-[24px] leading-tight text-brand-ink mb-3">
                    {it.name}
                  </h4>
                  <p className="font-cormorant text-body-md text-deep-taupe">
                    {it.note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Closing quote */}
      <section className="bg-cover-ivory px-margin-mobile md:px-margin-desktop py-narrative-gap">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal
            as="blockquote"
            className="font-cormorant text-[23px] md:text-[29px] leading-snug text-brand-ink italic"
          >
            &ldquo;{c.closing.quote}&rdquo;
          </Reveal>
          <Reveal
            as="p"
            delay={150}
            className="font-inter text-eyebrow text-brand-secondary uppercase mt-8 mb-12"
          >
            {c.closing.attribution}
          </Reveal>
          <Reveal delay={250}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-gold px-8 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                Enquire About Your Designed Menu
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {zoom && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in"
          onClick={() => setZoom(null)}
          data-testid="dish-lightbox"
        >
          <button
            onClick={() => setZoom(null)}
            className="absolute top-6 right-6 text-ink-on-dark/70 hover:text-ink-on-dark transition-colors"
            data-testid="dish-lightbox-close"
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>
          <figure className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={zoom.src}
              alt={zoom.alt}
              className="max-w-[92vw] max-h-[82vh] object-contain shadow-2xl"
              decoding="async"
              style={{ filter: "sepia(0.12) saturate(1.08) brightness(1.02)" }}
            />
            <figcaption className="font-inter text-eyebrow uppercase tracking-widest text-ink-on-dark/70 mt-5">
              {zoom.alt}
            </figcaption>
          </figure>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Culinary;
